// Multi-band onset envelope for the phase model (S9a).
//
// The tracker's own envelope (`dsp/onset.ts`) is one number per frame: log-mel spectral flux summed over 40 mel bands. The
// phase model needs to know WHICH instrument made the onset (the bass, the conga body, the bells and slap, the shakers), so the
// same flux is summed over four frequency groups instead. Everything else is as in `onset.ts` (1024-sample Hann window, 100
// frames per second, log compression of the band energies, positive change from the previous frame), so the two agree frame
// for frame: the four bands add up to the tracker's envelope before its normalisation.
//
// Each band's envelope is divided by its own mean over the audio, so the level of the recording and the balance of the bands
// do not matter downstream.

import { onsetAccumulator, type OnsetEnvelope } from '../dsp/onset.js';
import { powerSpectra, STFT_BINS, STFT_FRAME, stftShape, type StftShape } from '../dsp/stft.js';

export interface PhaseBand {
  readonly name: string;
  /** Lower and upper edge in Hz (a mel filter belongs to the band that holds its centre frequency). */
  readonly lo: number;
  readonly hi: number;
}

/** The four bands: bass, conga body, bells / slap / clave, and shakers (güiro, maracas). */
export const PHASE_BANDS: readonly PhaseBand[] = [
  { name: 'bass', lo: 40, hi: 200 },
  { name: 'lowmid', lo: 200, hi: 800 },
  { name: 'highmid', lo: 800, hi: 5000 },
  { name: 'top', lo: 5000, hi: Infinity },
];

export interface BandEnvelopes {
  readonly fps: number;
  /** Frame n is centred at time t0 + n / fps, in seconds. */
  readonly t0: number;
  readonly names: readonly string[];
  /** One envelope per band, each with mean 1 (all zeros for a silent band). */
  readonly env: readonly Float64Array[];
}

const MEL_BANDS = 48;
const FMIN = 40;
const FMAX = 16000;
const COMPRESSION = 100;

interface MelFilter { readonly lo: number; readonly w: Float64Array; readonly centreHz: number }

function melFilters(sampleRate: number): MelFilter[] {
  const hz = (m: number) => 700 * (10 ** (m / 2595) - 1);
  const mel = (f: number) => 2595 * Math.log10(1 + f / 700);
  const top = Math.min(FMAX, sampleRate / 2);
  const edges = Array.from({ length: MEL_BANDS + 2 }, (_, i) => hz(mel(FMIN) + ((mel(top) - mel(FMIN)) * i) / (MEL_BANDS + 1)));
  const bins = STFT_BINS;
  const out: MelFilter[] = [];
  for (let b = 0; b < MEL_BANDS; b++) {
    const [l, c, r] = [edges[b]!, edges[b + 1]!, edges[b + 2]!];
    const weight = (k: number) => {
      const f = (k * sampleRate) / STFT_FRAME;
      if (f > l && f <= c) return (f - l) / (c - l);
      if (f > c && f < r) return (r - f) / (r - c);
      return 0;
    };
    let lo = 0;
    while (lo < bins && weight(lo) === 0) lo++;
    let hi = lo;
    while (hi + 1 < bins && weight(hi + 1) > 0) hi++;
    out.push({ lo, w: Float64Array.from({ length: Math.max(0, hi - lo + 1) }, (_, j) => weight(lo + j)), centreHz: c });
  }
  return out;
}

/** Builds the band envelopes from the frames' power spectra, one `add` per frame in order (`powerSpectra`), then `finish`. */
export interface BandAccumulator {
  add(frame: number, power: Float64Array): void;
  finish(): BandEnvelopes;
}

export function bandAccumulator(shape: StftShape, sampleRate: number, bands: readonly PhaseBand[] = PHASE_BANDS): BandAccumulator {
  const { frames } = shape;
  const filters = melFilters(sampleRate);
  const group = filters.map((f) => bands.findIndex((b) => f.centreHz >= b.lo && f.centreHz < b.hi));
  let prev = new Float64Array(MEL_BANDS), cur = new Float64Array(MEL_BANDS);
  const env = bands.map(() => new Float64Array(frames));
  return {
    add(f, power) {
      for (let b = 0; b < MEL_BANDS; b++) {
        const { lo, w } = filters[b]!;
        let e = 0;
        for (let j = 0; j < w.length && lo + j < STFT_BINS; j++) e += w[j]! * power[lo + j]!;
        cur[b] = Math.log(1 + COMPRESSION * e);
      }
      if (f > 0) for (let b = 0; b < MEL_BANDS; b++) { const g = group[b]!; if (g >= 0) env[g]![f]! += Math.max(0, cur[b]! - prev[b]!); }
      [prev, cur] = [cur, prev];
    },
    finish() {
      for (const e of env) {
        let sum = 0;
        for (const v of e) sum += v;
        const mean = frames ? sum / frames : 0;
        if (mean > 0) for (let i = 0; i < frames; i++) e[i] = e[i]! / mean;
      }
      return { fps: shape.fps, t0: STFT_FRAME / 2 / sampleRate, names: bands.map((b) => b.name), env };
    },
  };
}

/** The multi-band onset envelope of mono audio. */
export function bandEnvelopes(x: ArrayLike<number>, sampleRate: number, bands: readonly PhaseBand[] = PHASE_BANDS): BandEnvelopes {
  const shape = stftShape(x.length, sampleRate);
  const acc = bandAccumulator(shape, sampleRate, bands);
  powerSpectra(x, shape, acc.add);
  return acc.finish();
}

/**
 * The onset envelope and the band envelopes of the same audio, from one pass of the short-time spectrum: the results are exactly those of
 * `onsetEnvelope` and `bandEnvelopes` called separately (same frames, same arithmetic), at the cost of one FFT per frame instead of two.
 */
export function analyseEnvelopes(x: ArrayLike<number>, sampleRate: number): { readonly onset: OnsetEnvelope; readonly bands: BandEnvelopes } {
  const shape = stftShape(x.length, sampleRate);
  const onset = onsetAccumulator(shape, sampleRate), bands = bandAccumulator(shape, sampleRate);
  powerSpectra(x, shape, (f, power) => { onset.add(f, power); bands.add(f, power); });
  return { onset: onset.finish(), bands: bands.finish() };
}
